import { parsePaymentTerms } from "./paymentTermsParser";
import type { MappedRow, CalculatedRow, BacktestSummary } from "@/types/backtest";

/** Parse a date string into ISO YYYY-MM-DD, returns null on failure */
function parseDate(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const s = raw.trim();

  // Already ISO YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  // YYYY/MM/DD
  const ymd = s.match(/^(\d{4})\/(\d{2})\/(\d{2})$/);
  if (ymd) {
    const iso = `${ymd[1]}-${ymd[2]}-${ymd[3]}`;
    const d = new Date(iso + "T00:00:00Z");
    if (!isNaN(d.getTime())) return iso;
  }

  // MM/DD/YYYY or M/D/YYYY (most common US AP export format)
  const mdy = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy) {
    const iso = `${mdy[3]}-${mdy[1].padStart(2, "0")}-${mdy[2].padStart(2, "0")}`;
    const d = new Date(iso + "T00:00:00Z");
    if (!isNaN(d.getTime())) return iso;
  }

  // MM-DD-YYYY
  const mdyDash = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (mdyDash) {
    const iso = `${mdyDash[3]}-${mdyDash[1].padStart(2, "0")}-${mdyDash[2].padStart(2, "0")}`;
    const d = new Date(iso + "T00:00:00Z");
    if (!isNaN(d.getTime())) return iso;
  }

  // Any other format – try native parsing (may be imprecise for non-ISO strings)
  const d = new Date(raw);
  if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);

  return null;
}

/** Add days to an ISO YYYY-MM-DD string, returning a new ISO string (UTC-safe) */
function addDays(isoDate: string, days: number): string {
  const d = new Date(isoDate + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function calculateRow(row: MappedRow): CalculatedRow {
  const invoiceDate = parseDate(row.invoiceDate);
  const actualPaymentDate = parseDate(row.actualPaymentDate);
  const parsedTerms = parsePaymentTerms(row.paymentTerms);

  const errors: string[] = [];
  if (!invoiceDate) errors.push("invalid invoice date");
  if (!actualPaymentDate) errors.push("invalid payment date");
  if (row.invoiceAmount == null || isNaN(row.invoiceAmount)) errors.push("missing amount");

  if (errors.length > 0) {
    return {
      ...row,
      parsedTerms: null,
      discountDeadline: null,
      wasEligible: false,
      discountCaptured: false,
      missedSavings: 0,
      capturedSavings: 0,
      parseError: errors.join("; "),
    };
  }

  if (!parsedTerms) {
    return {
      ...row,
      invoiceDate,
      actualPaymentDate,
      parsedTerms: null,
      discountDeadline: null,
      wasEligible: false,
      discountCaptured: false,
      missedSavings: 0,
      capturedSavings: 0,
      parseError: null,
    };
  }

  const discountDeadline = addDays(invoiceDate!, parsedTerms.discountDays);
  const captured = actualPaymentDate! <= discountDeadline;
  const discountValue =
    Math.round((row.invoiceAmount ?? 0) * (parsedTerms.discountPct / 100) * 100) / 100;

  return {
    ...row,
    invoiceDate,
    actualPaymentDate,
    parsedTerms,
    discountDeadline,
    wasEligible: true,
    discountCaptured: captured,
    missedSavings: captured ? 0 : discountValue,
    capturedSavings: captured ? discountValue : 0,
    parseError: null,
  };
}

export function calculateAll(rows: MappedRow[]): CalculatedRow[] {
  return rows.map(calculateRow);
}

export function calculateSummary(rows: CalculatedRow[]): BacktestSummary {
  const eligible = rows.filter((r) => r.wasEligible);
  const captured = eligible.filter((r) => r.discountCaptured);
  const missed = eligible.filter((r) => !r.discountCaptured);

  const totalMissed = Math.round(missed.reduce((s, r) => s + r.missedSavings, 0) * 100) / 100;
  const totalCaptured = Math.round(captured.reduce((s, r) => s + r.capturedSavings, 0) * 100) / 100;

  const dates = rows
    .filter((r) => r.invoiceDate)
    .map((r) => r.invoiceDate as string)
    .sort();

  return {
    totalInvoices: rows.length,
    eligibleInvoices: eligible.length,
    discountsCaptured: captured.length,
    discountsMissed: missed.length,
    totalMissedSavings: totalMissed,
    totalCapturedSavings: totalCaptured,
    captureRate: eligible.length > 0 ? (captured.length / eligible.length) * 100 : 0,
    dateRangeStart: dates[0] ?? null,
    dateRangeEnd: dates[dates.length - 1] ?? null,
  };
}
